import {
  BoldOutlined,
  ClearOutlined,
  FileImageOutlined,
  InsertRowBelowOutlined,
  ItalicOutlined,
  LinkOutlined,
  OrderedListOutlined,
  UnderlineOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { App, Button, Input, Modal, Segmented, Space, Typography } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { uploadFileToOss } from '@/utils/oss';

type UploadFileHandler = (file: File) => Promise<string>;

interface RichTextEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  minHeight?: number;
  uploadFile?: UploadFileHandler;
}

type EditorMode = 'visual' | 'html';

const RichTextEditor = ({
  value,
  onChange,
  placeholder = '请输入内容...',
  minHeight = 360,
  uploadFile,
}: RichTextEditorProps) => {
  const { message } = App.useApp();
  const editorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const selectionRef = useRef<Range | null>(null);
  const syncedHtmlRef = useRef(value || '');
  const [mode, setMode] = useState<EditorMode>('visual');
  const [htmlValue, setHtmlValue] = useState(value || '');
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [imageOpen, setImageOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const uploadHandler =
    uploadFile ||
    (async (file: File) => {
      const result = await uploadFileToOss(file, {
        dir: 'shop/rich-text/',
      });
      return result.url;
    });

  useEffect(() => {
    const nextValue = value || '';
    if (nextValue === syncedHtmlRef.current) {
      return;
    }

    syncedHtmlRef.current = nextValue;
    setHtmlValue(nextValue);
  }, [value]);

  useEffect(() => {
    if (
      mode === 'visual' &&
      editorRef.current &&
      editorRef.current.innerHTML !== htmlValue
    ) {
      editorRef.current.innerHTML = htmlValue;
    }
  }, [htmlValue, mode]);

  const emitChange = (nextValue: string) => {
    syncedHtmlRef.current = nextValue;
    setHtmlValue(nextValue);
    onChange?.(nextValue);
  };

  const updateFromEditor = () => {
    const nextValue = editorRef.current?.innerHTML || '';
    emitChange(nextValue);
  };

  const rememberSelection = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return;
    }

    selectionRef.current = selection.getRangeAt(0).cloneRange();
  };

  const restoreSelection = () => {
    const selection = window.getSelection();
    if (!selection || !selectionRef.current) {
      editorRef.current?.focus();
      return;
    }

    selection.removeAllRanges();
    selection.addRange(selectionRef.current);
  };

  const executeCommand = (command: string, commandValue?: string) => {
    editorRef.current?.focus();
    restoreSelection();

    // 这里使用浏览器原生编辑命令，优先保证无额外依赖也能落地富文本能力。
    document.execCommand(command, false, commandValue);
    updateFromEditor();
    rememberSelection();
  };

  const insertHtml = (html: string) => {
    executeCommand('insertHTML', html);
  };

  const openLinkDialog = () => {
    const selectionText = window.getSelection()?.toString() || '';
    setLinkUrl(selectionText.startsWith('http') ? selectionText : '');
    setLinkOpen(true);
  };

  const toolbarActions = useMemo(
    () => [
      {
        key: 'paragraph',
        label: '正文',
        onClick: () => executeCommand('formatBlock', 'P'),
      },
      {
        key: 'h2',
        label: '标题 2',
        onClick: () => executeCommand('formatBlock', 'H2'),
      },
      {
        key: 'h3',
        label: '标题 3',
        onClick: () => executeCommand('formatBlock', 'H3'),
      },
      {
        key: 'bold',
        label: '加粗',
        icon: <BoldOutlined />,
        onClick: () => executeCommand('bold'),
      },
      {
        key: 'italic',
        label: '斜体',
        icon: <ItalicOutlined />,
        onClick: () => executeCommand('italic'),
      },
      {
        key: 'underline',
        label: '下划线',
        icon: <UnderlineOutlined />,
        onClick: () => executeCommand('underline'),
      },
      {
        key: 'unorderedList',
        label: '无序列表',
        icon: <UnorderedListOutlined />,
        onClick: () => executeCommand('insertUnorderedList'),
      },
      {
        key: 'orderedList',
        label: '有序列表',
        icon: <OrderedListOutlined />,
        onClick: () => executeCommand('insertOrderedList'),
      },
      {
        key: 'quote',
        label: '引用',
        onClick: () => executeCommand('formatBlock', 'BLOCKQUOTE'),
      },
      {
        key: 'divider',
        label: '分割线',
        icon: <InsertRowBelowOutlined />,
        onClick: () => executeCommand('insertHorizontalRule'),
      },
      {
        key: 'link',
        label: '链接',
        icon: <LinkOutlined />,
        onClick: openLinkDialog,
      },
      {
        key: 'image',
        label: '图片',
        icon: <FileImageOutlined />,
        onClick: () => setImageOpen(true),
      },
      {
        key: 'clear',
        label: '清除格式',
        icon: <ClearOutlined />,
        onClick: () => {
          executeCommand('removeFormat');
          executeCommand('unlink');
        },
      },
    ],
    [],
  );

  return (
    <div className="zhiht-rich-editor">
      <div className="zhiht-rich-editor-toolbar">
        <Space wrap>
          {toolbarActions.map((action) => (
            <Button
              key={action.key}
              size="small"
              icon={action.icon}
              onMouseDown={(event) => event.preventDefault()}
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          ))}
          <Button
            size="small"
            icon={<FileImageOutlined />}
            loading={uploading}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => fileInputRef.current?.click()}
          >
            上传图片
          </Button>
        </Space>

        <Segmented<EditorMode>
          options={[
            { label: '可视化', value: 'visual' },
            { label: 'HTML', value: 'html' },
          ]}
          value={mode}
          onChange={(nextValue) => setMode(nextValue as EditorMode)}
        />
      </div>

      {mode === 'visual' ? (
        <div
          ref={editorRef}
          className="zhiht-rich-editor-surface"
          contentEditable
          data-placeholder={placeholder}
          style={{ minHeight }}
          suppressContentEditableWarning
          onBlur={rememberSelection}
          onInput={updateFromEditor}
          onKeyUp={rememberSelection}
          onMouseUp={rememberSelection}
        />
      ) : (
        <Input.TextArea
          autoSize={false}
          className="zhiht-rich-editor-source"
          style={{ minHeight }}
          value={htmlValue}
          onChange={(event) => {
            const nextValue = event.target.value;
            emitChange(nextValue);
          }}
        />
      )}

      <Typography.Text type="secondary">
        支持图片上传和网络图片插入。当前开发环境默认把本地图片转成 Data URL，
        后续接入 OSS 时只需要替换上传适配器。
      </Typography.Text>

      <input
        ref={fileInputRef}
        hidden
        accept="image/*"
        type="file"
        onChange={async (event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (!file) {
            return;
          }

          try {
            setUploading(true);
            const url = await uploadHandler(file);
            insertHtml(`<p><img src="${url}" alt="${file.name}" /></p>`);
          } catch (error) {
            message.error((error as Error).message || '图片上传失败');
          } finally {
            setUploading(false);
          }
        }}
      />

      <Modal
        destroyOnHidden
        open={linkOpen}
        title="插入链接"
        onCancel={() => setLinkOpen(false)}
        onOk={() => {
          const nextUrl = linkUrl.trim();
          if (!nextUrl) {
            message.warning('请输入链接地址');
            return;
          }

          executeCommand('createLink', nextUrl);
          setLinkOpen(false);
        }}
      >
        <Input
          allowClear
          placeholder="请输入链接地址"
          value={linkUrl}
          onChange={(event) => setLinkUrl(event.target.value)}
        />
      </Modal>

      <Modal
        destroyOnHidden
        open={imageOpen}
        title="插入图片"
        onCancel={() => setImageOpen(false)}
        onOk={() => {
          const nextUrl = imageUrl.trim();
          if (!nextUrl) {
            message.warning('请输入图片地址');
            return;
          }

          insertHtml(`<p><img src="${nextUrl}" alt="插入图片" /></p>`);
          setImageOpen(false);
          setImageUrl('');
        }}
      >
        <Input
          allowClear
          placeholder="请输入图片 URL"
          value={imageUrl}
          onChange={(event) => setImageUrl(event.target.value)}
        />
      </Modal>
    </div>
  );
};

export default RichTextEditor;
